import type {
  AdjustmentRequest,
  AdjustmentResponse,
  BalanceMatrixRow,
  BalancesListResponse,
  BalancesMatrixResponse,
  DefaultsRow,
  DefaultsUpdate,
  DefaultsUpdateResponse,
  OverridesListResponse,
  OverrideCreateRequest,
  OverrideCreateResponse,
  OverrideDeleteResponse,
  RolloverPlan,
  RolloverRequest,
  RolloverResult,
} from '@lms/shared-types'
import { apiRequest } from './client'

export function getDefaults(): Promise<DefaultsRow[]> {
  return apiRequest<DefaultsRow[]>('/api/v1/leave-credits/defaults')
}

export function updateDefault(input: DefaultsUpdate): Promise<DefaultsUpdateResponse> {
  return apiRequest<DefaultsUpdateResponse>('/api/v1/leave-credits/defaults', {
    method: 'PATCH',
    body: input,
  })
}

export function getOverrides(
  params: { limit?: number; before?: string } = {},
): Promise<OverridesListResponse> {
  const q = new URLSearchParams()
  if (params.limit !== undefined) q.set('limit', String(params.limit))
  if (params.before !== undefined) q.set('before', params.before)
  const qs = q.toString()
  return apiRequest<OverridesListResponse>(
    `/api/v1/leave-credits/overrides${qs ? `?${qs}` : ''}`,
  )
}

export function createOverride(
  input: OverrideCreateRequest,
): Promise<OverrideCreateResponse> {
  return apiRequest<OverrideCreateResponse>('/api/v1/leave-credits/overrides', {
    method: 'POST',
    body: input,
  })
}

export function deleteOverride(id: string): Promise<OverrideDeleteResponse> {
  return apiRequest<OverrideDeleteResponse>(
    `/api/v1/leave-credits/overrides/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  )
}

export function previewRollover(input: RolloverRequest): Promise<RolloverPlan> {
  return apiRequest<RolloverPlan>('/api/v1/leave-credits/rollover/preview', {
    method: 'POST',
    body: input,
  })
}

export function runRollover(input: RolloverRequest): Promise<RolloverResult> {
  return apiRequest<RolloverResult>('/api/v1/leave-credits/rollover/run', {
    method: 'POST',
    body: input,
  })
}

// ── Per-employee balance admin (Leave-credits v2) ────────────────────────────

export function getBalances(params: {
  leaveType: string
  year?: number
  q?: string
  includeIneligible?: boolean
  limit?: number
  before?: string
}): Promise<BalancesListResponse> {
  const qp = new URLSearchParams()
  qp.set('leave_type', params.leaveType)
  if (params.year !== undefined) qp.set('year', String(params.year))
  if (params.q) qp.set('q', params.q)
  if (params.includeIneligible !== undefined) qp.set('include_ineligible', String(params.includeIneligible))
  if (params.limit !== undefined) qp.set('limit', String(params.limit))
  if (params.before) qp.set('before', params.before)
  return apiRequest<BalancesListResponse>(`/api/v1/leave-credits/balances?${qp.toString()}`)
}

export function getBalancesMatrix(params: {
  year?: number
  q?: string
  limit?: number
  before?: string
  /** 1-based page for numbered (offset) pagination. When set, the response
   *  carries `total` and the server ignores `before`. */
  page?: number
}): Promise<BalancesMatrixResponse> {
  const qp = new URLSearchParams()
  if (params.year !== undefined) qp.set('year', String(params.year))
  if (params.q) qp.set('q', params.q)
  if (params.limit !== undefined) qp.set('limit', String(params.limit))
  if (params.before) qp.set('before', params.before)
  if (params.page !== undefined) qp.set('page', String(params.page))
  const qs = qp.toString()
  return apiRequest<BalancesMatrixResponse>(
    `/api/v1/leave-credits/balances/matrix${qs ? `?${qs}` : ''}`,
  )
}

/** Page through the whole balances matrix for the given year/search and return
 *  every row. Used by the roster's CSV export, which must cover all employees,
 *  not just the visible page. Pages at the endpoint's max limit (200, numbered
 *  offset) and stops once `total` rows are collected, with a hard page cap as a
 *  runaway guard. */
export async function getAllBalancesMatrix(params: {
  year?: number
  q?: string
}): Promise<BalanceMatrixRow[]> {
  const PAGE_LIMIT = 200
  const MAX_PAGES = 200 // 40k employees — far above any real roster
  const all: BalanceMatrixRow[] = []
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const r = await getBalancesMatrix({ ...params, limit: PAGE_LIMIT, page })
    all.push(...r.items)
    const total = r.total ?? all.length
    if (all.length >= total || r.items.length < PAGE_LIMIT) break
  }
  return all
}

export function applyAdjustment(input: AdjustmentRequest): Promise<AdjustmentResponse> {
  return apiRequest<AdjustmentResponse>('/api/v1/leave-credits/adjustments', {
    method: 'POST',
    body: input,
  })
}
